// frontend/src/visitor_ai/components/VisitorAIChat.jsx
import React, { useState } from "react";
import { useVisitorAIContext } from "../context/VisitorAIContext";
import useVisitorAI from "../hooks/useVisitorAI";
import VisitorReportTable from "./VisitorReportTable";
import ActionConfirmBox from "./ActionConfirmBox";
import MissingInfoForm from "./MissingInfoForm";
import CreateVisitAssistant from "./CreateVisitAssistant";
import DisambiguationPanel from "./DisambiguationPanel";
import QuickActionButton from "./QuickActionButton";
import "../styles/visitorAI.css";
import { VISITOR_AI_WELCOME_MESSAGE } from "../visitorAI.constants";

const VisitorAIChat = ({ pageContext = {} }) => {
  const {
    isOpen, setIsOpen,
    messages, addMessage,
    loading, setLoading,
    pendingAction, setPendingAction,
    retrievalHint, setRetrievalHint,
    missingFields, setMissingFields
  } = useVisitorAIContext();

  const { sendMessage, executeAction } = useVisitorAI();
  const [input, setInput] = useState("");

  // greet on first open
  const handleOpen = () => {
    setIsOpen(true);
    if (messages.length === 0) addMessage({ id: "welcome", sender: "ai", text: VISITOR_AI_WELCOME_MESSAGE });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    try {
      await sendMessage(text, pageContext);
    } catch (err) {
      // error message pushed from hook
    }
    setInput("");
  };

  const onConfirmAction = async (actionSchema) => {
    try {
      setLoading(true);
      await executeAction(actionSchema, pageContext, true);
      setPendingAction(null);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const onCancelAction = () => {
    setPendingAction(null);
  };

  const onSelectDisambiguation = async (opt) => {
    // send selected entity back to AI as a short text for follow-up
    const txt = `I choose ${opt.name} (id:${opt.id || opt.visit_id || ""})`;
    await sendMessage(txt, pageContext);
  };

  // Quick action clicked from table row
  const onQuickAction = async (actionSchema) => {
    // If action requires confirm, show confirm box
    if (actionSchema?.confirm_required) {
      setPendingAction(actionSchema);
      return;
    }
    // else execute directly
    await executeAction(actionSchema, pageContext, true);
  };

  return (
    <>
      {/* Floating AI Bubble */}
      <div className="visitor-ai-bubble" onClick={handleOpen}>🤖</div>

      {/* Chat Window */}
      {isOpen && (
        <div className="visitor-ai-container">
          <div className="visitor-ai-header">
            <span>Visitor AI Assistant</span>
            <button className="visitor-ai-close" onClick={() => setIsOpen(false)}>✖</button>
          </div>

          <div className="visitor-ai-body">
            <div className="visitor-ai-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`visitor-ai-message ${msg.sender === "user" ? "user" : msg.sender === "system" ? "system" : "ai"}`}>
                  <div className="visitor-ai-message-text">{msg.text}</div>

                  {/* show retrieval hint if message carries one */}
                  {msg.retrieval_hint && (
                    <div className="visitor-ai-retrieval">
                      <strong>AI applied filters:</strong> {JSON.stringify(msg.retrieval_hint)}
                    </div>
                  )}

                  {/* show result table */}
                  {msg.result && msg.result.length > 0 && msg.ui_hint?.table && (
                    <VisitorReportTable
                      rows={msg.result}
                      columns={msg.ui_hint.columns}
                      onRowAction={onQuickAction}
                    />
                  )}

                  {/* if AI returned raw rows but no ui_hint, show basic table */}
                  {msg.result && msg.result.length > 0 && !msg.ui_hint?.table && (
                    <pre className="visitor-ai-table-pre">{JSON.stringify(msg.result, null, 2)}</pre>
                  )}

                  {/* action schema quick button (if not confirm-required) */}
                  {msg.action_schema && !msg.action_schema.confirm_required && (
                    <QuickActionButton
                      label={msg.action_schema.label || msg.action_schema.action_type}
                      actionSchema={msg.action_schema}
                      onClick={onQuickAction}
                    />
                  )}

                  {/* create visit form if AI asks for create and returns params */}
                  {msg.action_schema && msg.action_schema.action_type === "create" && (
                    <CreateVisitAssistant
                      initialValues={msg.action_schema.params || {}}
                      onSubmit={async (formValues) => {
                        // send form values as text to AI (the orchestrator expects conversational input)
                        await sendMessage(JSON.stringify(formValues), pageContext);
                      }}
                    />
                  )}

                  {/* needs_info flow */}
                  {msg.status === "needs_info" && (
                    <MissingInfoForm
                      missingFields={msg.missing_fields || msg.action_schema?.missing_fields || []}
                      onSubmit={async (answers) => {
                        // convert answers to conversational text and re-send
                        const kvPairs = Object.entries(answers).map(([k, v]) => `${k}: ${v}`).join(", ");
                        await sendMessage(kvPairs, pageContext);
                      }}
                    />
                  )}

                  {/* disambiguation */}
                  {msg.ui_hint?.disambiguation && (
                    <DisambiguationPanel
                      options={msg.ui_hint.disambiguation}
                      onSelect={onSelectDisambiguation}
                    />
                  )}
                </div>
              ))}

              {loading && <div className="visitor-ai-loading">Processing...</div>}
            </div>

            {/* Side panel for retrieval hint / pending action */}
            <div className="visitor-ai-side">
              {retrievalHint && (
                <div className="visitor-ai-hint">
                  <strong>Retrieval:</strong> {JSON.stringify(retrievalHint)}
                </div>
              )}

              {pendingAction && (
                <ActionConfirmBox
                  actionSchema={pendingAction}
                  onConfirm={() => onConfirmAction(pendingAction)}
                  onCancel={onCancelAction}
                />
              )}
            </div>
          </div>

          <div className="visitor-ai-input">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Visitor AI (e.g. show pending approvals for today)..."
              onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
            />
            <button className="visitor-ai-send" onClick={handleSend}>➤</button>
          </div>
        </div>
      )}
    </>
  );
};

export default VisitorAIChat;
